"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cpu, ToggleLeft, Activity, ShieldAlert, Wind, Wrench, ArrowRight, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const componentCategories = [{
  code: "SEC-01",
  name: "Controllers",
  desc: "Primary operator consoles, emergency stop circuits, variable frequency drives.",
  icon: Cpu
}, {
  code: "SEC-02",
  name: "Switches",
  desc: "Heavy-duty limit switches, toggle controls, safety interlock relays.",
  icon: ToggleLeft
}, {
  code: "SEC-03",
  name: "Sensors",
  desc: "Motion position sensors, optical gate counters, rotation encoders.",
  icon: Activity
}, {
  code: "SEC-04",
  name: "Inflatable Components",
  desc: "Landing mats, baffled impact walls, commercial vinyl seams.",
  icon: ShieldAlert
}, {
  code: "SEC-05",
  name: "Blowers",
  desc: "Continuous-duty radial inflation motors, back-draft flaps.",
  icon: Wind
}, {
  code: "SEC-06",
  name: "Model-Specific Parts",
  desc: "Proprietary chassis bushings, hydraulic seals, custom drive couplings.",
  icon: Wrench
}];
export default function ReplacementParts() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="checkreplacementparts" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 border-y border-border overflow-hidden">
      {/* Background Architectural Grid Pattern (Hairline / Industrial) */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)] bg-[size:32px_32px]" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-6xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="space-y-10">
          {/* Main Spec Plate Container with Machined Corner Datum Marks */}
          <div className="relative bg-card text-card-foreground border border-border p-6 sm:p-10 md:p-12 shadow-sm">
            {/* Top-Left Corner Datum */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-foreground/70" aria-hidden="true" />
            {/* Top-Right Corner Datum */}
            <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-foreground/70" aria-hidden="true" />
            {/* Bottom-Left Corner Datum */}
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-foreground/70" aria-hidden="true" />
            {/* Bottom-Right Corner Datum */}
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-foreground/70" aria-hidden="true" />

            {/* Header Block */}
            <div className="space-y-4 max-w-3xl">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-muted text-foreground font-mono text-xs tracking-wider uppercase border border-border">
                  <AlertTriangle className="size-3 text-primary" />
                  Parts Advisory &amp; Logistics
                </span>
                <span className="font-mono text-xs text-muted-foreground tracking-widest hidden sm:inline-block">
                  DOC-ID: MBS-SEC-09
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold uppercase tracking-tight text-foreground leading-none">
                Check Replacement Parts Before You Need Them
              </h2>
            </div>

            {/* Dictated Narrative Copy */}
            <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-6 text-sm sm:text-base font-sans text-muted-foreground leading-relaxed">
              <p className="border-l-2 border-primary/60 pl-3">
                Parts availability can affect the long-term value of a mechanical bull. A relatively small unavailable component can create expensive downtime for a commercial operator.
              </p>
              <p className="border-l-2 border-border pl-3">
                Before purchasing, consider whether compatible controllers, switches, sensors, inflatable components, blowers and model-specific parts can still be sourced.
              </p>
              <p className="border-l-2 border-border pl-3 font-medium text-foreground">
                This matters especially with older or less common machines.
              </p>
            </div>

            {/* Component Spec Table / Checklist */}
            <div className="mt-10 pt-8 border-t border-border">
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
                  Critical Subsystem Verification Checklist
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  6 Core Sourcing Points
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-border bg-background">
                {componentCategories.map((item, i) => {
                const Icon = item.icon;
                return <motion.div key={item.code} data-index={i} variants={itemVariants} className="border-r border-b border-border p-5 flex flex-col justify-between hover:bg-card transition-colors">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="size-8 rounded-none border border-border bg-muted flex items-center justify-center text-primary">
                            <Icon className="size-4" />
                          </div>
                          <span className="font-mono text-xs text-muted-foreground font-semibold">
                            {item.code}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-base font-serif font-bold uppercase tracking-wide text-foreground">
                            {item.name}
                          </h3>
                          <p className="text-xs text-muted-foreground font-sans mt-1 leading-snug">
                            {item.desc}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between text-[11px] font-mono text-muted-foreground">
                        <span>Status check:</span>
                        <span className="text-foreground font-medium">Verify Lead Time</span>
                      </div>
                    </motion.div>;
              })}
              </div>
            </div>

            {/* Technical Directive Footer & Action CTA */}
            <div className="mt-10 pt-8 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div className="space-y-1">
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  Mechanical Bull Shop Logistics Unit
                </p>
                <p className="text-xs text-foreground/80 font-sans">
                  Inspect parts schematics, replacement electrical components, and inflation gear.
                </p>
              </div>

              <div>
                <Button asChild size="lg" className="w-full sm:w-auto font-serif uppercase tracking-wider text-sm font-semibold rounded-none">
                  <Link to="/MechanicalBullPartsAndAccessoriesPage">
                    <span>Browse Parts &amp; Accessories</span>
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}